import { useState, useEffect, useRef } from "react";
import { createClient } from '@supabase/supabase-js';
import AuthPage from './pages/AuthPage';
import OnboardingPage from './pages/OnboardingPage';
import Dashboard from './pages/Dashboard';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);
export { supabase };

const STRIPE_LINKS = {
  basic: "https://buy.stripe.com/bJebJ0dNEcxT3Eu1HggQE03",
  pro: "https://buy.stripe.com/8x23cu24WbtPfnceu2gQE04",
  elite: "https://buy.stripe.com/eVq28q3900Pb3Eu99IgQE05"
};

const MOCK_TRADES = [
  { sport: "NBA", team: "Boston Celtics", side: "YES", entry: 42, exit: 88, pct: "+110%", time: "2m ago" },
  { sport: "MLB", team: "San Diego Padres", side: "YES", entry: 43, exit: 76, pct: "+77%", time: "8m ago" },
  { sport: "NHL", team: "NY Rangers", side: "NO", entry: 38, exit: 72, pct: "+89%", time: "14m ago" },
  { sport: "Tennis", team: "Andreeva", side: "YES", entry: 44, exit: 81, pct: "+84%", time: "21m ago" },
  { sport: "MLB", team: "Kansas City Royals", side: "YES", entry: 43, exit: 74, pct: "+72%", time: "35m ago" },
  { sport: "Soccer", team: "Arsenal", side: "YES", entry: 47, exit: 89, pct: "+89%", time: "41m ago" },
  { sport: "NBA", team: "Denver Nuggets", side: "NO", entry: 39, exit: 78, pct: "+100%", time: "58m ago" },
  { sport: "MLB", team: "Baltimore Orioles", side: "NO", entry: 41, exit: 70, pct: "+71%", time: "1h ago" },
];

const SPORT_COLORS = { NBA: "#ff6b35", MLB: "#00d4ff", NHL: "#a78bfa", Soccer: "#34d399", Tennis: "#fbbf24", NCAAB: "#f87171" };

function Ticker() {
  const [offset, setOffset] = useState(0);
  const items = [...MOCK_TRADES, ...MOCK_TRADES];
  useEffect(() => {
    const id = setInterval(() => setOffset(o => (o + 0.4) % (MOCK_TRADES.length * 220)), 16);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ overflow: "hidden", background: "#0a0a0a", borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", padding: "10px 0" }}>
      <div style={{ display: "flex", transform: `translateX(-${offset}px)`, whiteSpace: "nowrap" }}>
        {items.map((t, i) => (
          <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 24px", borderRight: "1px solid #1a1a1a", minWidth: 220 }}>
            <span style={{ background: SPORT_COLORS[t.sport] + "22", color: SPORT_COLORS[t.sport], fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4 }}>{t.sport}</span>
            <span style={{ color: "#ccc", fontSize: 12 }}>{t.team}</span>
            <span style={{ color: "#00ff88", fontSize: 12, fontWeight: 700 }}>{t.pct}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveFeed() {
  const [trades, setTrades] = useState(MOCK_TRADES.slice(0, 5));
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setPulse(true);
      setTimeout(() => setPulse(false), 600);
      setTrades([...MOCK_TRADES].sort(() => Math.random() - 0.5).slice(0, 5));
    }, 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ background: "#0d0d0d", border: "1px solid #1e1e1e", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #1e1e1e" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: pulse ? "#ff4444" : "#00ff88", transition: "background 0.3s", boxShadow: pulse ? "0 0 8px #ff4444" : "0 0 8px #00ff88" }} />
          <span style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>Live Trade Feed</span>
        </div>
        <span style={{ color: "#444", fontSize: 11 }}>SCANNING 20+ MARKETS</span>
      </div>
      {trades.map((t, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: i < 4 ? "1px solid #141414" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: SPORT_COLORS[t.sport] + "18", color: SPORT_COLORS[t.sport], fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, minWidth: 46, textAlign: "center" }}>{t.sport}</span>
            <div>
              <div style={{ color: "#e0e0e0", fontSize: 13, fontWeight: 500 }}>{t.team} {t.side}</div>
              <div style={{ color: "#444", fontSize: 11 }}>{t.entry}¢ → {t.exit}¢ · {t.time}</div>
            </div>
          </div>
          <span style={{ color: "#00ff88", fontWeight: 700, fontSize: 14 }}>{t.pct}</span>
        </div>
      ))}
    </div>
  );
}

function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0; const step = target / 60;
        const id = setInterval(() => { start = Math.min(start + step, target); setVal(Math.floor(start)); if (start >= target) clearInterval(id); }, 16);
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

function PricingCard({ tier, price, features, cta, link, highlight, onGetStarted }) {
  return (
    <div style={{ border: highlight ? "1px solid #00ff88" : "1px solid #1e1e1e", borderRadius: 12, padding: "32px 28px", background: highlight ? "#061208" : "#0a0a0a", position: "relative", flex: 1, minWidth: 260 }}>
      {highlight && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "#00ff88", color: "#000", fontSize: 11, fontWeight: 800, padding: "3px 14px", borderRadius: 20, whiteSpace: "nowrap" }}>MOST POPULAR</div>}
      <div style={{ color: "#888", fontSize: 12, fontWeight: 600, letterSpacing: 2, marginBottom: 8 }}>{tier.toUpperCase()}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
        <span style={{ color: "#fff", fontSize: 42, fontWeight: 800 }}>${price}</span>
        <span style={{ color: "#555", fontSize: 14 }}>/mo</span>
      </div>
      <div style={{ height: 1, background: "#1e1e1e", margin: "20px 0" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
        {features.map((f, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, color: "#aaa", fontSize: 13 }}><span style={{ color: "#00ff88", flexShrink: 0 }}>✓</span>{f}</div>)}
      </div>
      <a href={link} target="_blank" rel="noreferrer" style={{ display: "block", textAlign: "center", padding: "13px", background: highlight ? "#00ff88" : "transparent", border: highlight ? "none" : "1px solid #333", color: highlight ? "#000" : "#fff", borderRadius: 8, fontWeight: 700, fontSize: 14, textDecoration: "none" }}>{cta}</a>
    </div>
  );
}

const LANDING_TRADES = [
  { sport: 'NBA', team: 'Boston Celtics YES', change: '+110%', from: '42c', to: '88c' },
  { sport: 'MLB', team: 'San Diego Padres YES', change: '+77%', from: '43c', to: '76c' },
  { sport: 'NHL', team: 'NY Rangers NO', change: '+89%', from: '38c', to: '72c' },
  { sport: 'Tennis', team: 'Andreeva YES', change: '+84%', from: '44c', to: '81c' },
  { sport: 'MLB', team: 'KC Royals YES', change: '+72%', from: '43c', to: '74c' },
  { sport: 'NBA', team: 'Denver Nuggets NO', change: '+100%', from: '50c', to: '100c' },
];
const SPORT_CLR = { NBA: '#F97316', MLB: '#3B82F6', NHL: '#06B6D4', Tennis: '#A855F7' };
const LP = { bg: '#060608', card: '#0d0d12', border: '#1a1a28', green: '#00e87a', blue: '#3b82f6', purple: '#8b5cf6', text: '#f0f0f8', muted: '#6b7280', white: '#ffffff' };
const LPLANS = [
  { name: 'Starter', price: 79, color: '#3b82f6', annualUrl: 'https://buy.stripe.com/3cI8wOfVMapL3EuadMgQE06', url: process.env.REACT_APP_STRIPE_BASIC_URL||'#', desc: 'Game winners across NBA, MLB and NHL', features: ['Game winner markets', 'Up to 50 trades/day', 'Live dashboard', 'Stop loss protection', 'Email support'] },
  { name: 'Pro', price: 149, color: '#00e87a', popular: true, annualUrl: 'https://buy.stripe.com/4gM3cubFwapLgrgadMgQE07', url: process.env.REACT_APP_STRIPE_PRO_URL||'#', desc: 'Add player props for deeper coverage', features: ['Everything in Starter', 'Player props (points, rebounds, HR, strikeouts +more)', 'Up to 100 trades/day', 'Advanced risk controls', 'Priority support'] },
  { name: 'Elite', price: 249, color: '#8b5cf6', annualUrl: 'https://buy.stripe.com/00w8wOfVMfK53EuadMgQE08', url: process.env.REACT_APP_STRIPE_ELITE_URL||'#', desc: 'Full coverage across every market', features: ['Everything in Pro', 'Spreads, totals, YRFI, overtime', 'Up to 200 trades/day', 'Live bankroll management', 'Discord VIP + direct support'] },
];
const LFAQS = [
  { q: 'How does KalshiBot Pro find edges?', a: 'The bot pulls live ESPN data every 15 seconds. It calculates real win probability from live score, time remaining, and momentum then compares to Kalshi prices. When the gap is 10%+ after fees, it trades automatically.' },
  { q: 'Do I need a Kalshi account?', a: 'Yes. Kalshi is CFTC-regulated, same oversight as the NYSE. You connect your API key and the bot trades on your account. You keep 100% of everything it makes.' },
  { q: 'What markets does the bot trade?', a: 'Game winners, player props (points, rebounds, assists, HR, strikeouts, goals, steals, blocks), game lines (spreads, totals, YRFI, first 5 innings, overtime) across NBA, MLB, and NHL.' },
  { q: 'How do I get started?', a: 'Choose a plan, upload your Kalshi API key and PEM file, select your risk profile. Bot starts automatically. Takes about 5 minutes.' },
  { q: 'Can I cancel anytime?', a: 'Yes. No contracts, no lock-in. Cancel from your account settings at any time.' },
];

function LandingPage({ onSignIn }) {
  const [hovered, setHovered] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [tradeIdx, setTradeIdx] = useState(0);
  const [annual, setAnnual] = useState(false);
  // Detect and store affiliate referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('kalshi_ref', ref);
    }
  }, []);
  useEffect(() => { const t = setInterval(() => setTradeIdx(i => (i+1)%LANDING_TRADES.length), 2000); return () => clearInterval(t); }, []);
  const G = LP.green; const M = LP.muted; const CA = LP.card;
  return (
    <div style={{ minHeight:'100vh', background:LP.bg, color:LP.text, fontFamily:'"DM Sans",system-ui,sans-serif', overflowX:'hidden' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800&display=swap'); @keyframes ticker{from{transform:translateX(0)}to{transform:translateX(-50%)}} @keyframes glow{0%,100%{opacity:1}50%{opacity:0.4}} *{box-sizing:border-box}`}</style>
      <nav style={{ padding:'0 48px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:'1px solid #1a1a28', position:'sticky', top:0, background:'#060608ee', backdropFilter:'blur(12px)', zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{fontSize:20}}>⚡</span>
          <span style={{ fontWeight:800, fontSize:18, color:LP.white, letterSpacing:-0.5 }}>KalshiBot <span style={{color:G}}>Pro</span></span>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <a href="https://discord.gg/H6KyBgP338" target="_blank" rel="noopener noreferrer" style={{ background:'#5865F2', color:'#fff', padding:'7px 16px', borderRadius:8, fontWeight:600, fontSize:13, textDecoration:'none' }}>Discord</a>
          <button onClick={onSignIn} style={{ background:'transparent', color:M, border:'1px solid #1a1a28', padding:'7px 18px', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:13 }}>Sign In</button>
          <button onClick={onSignIn} style={{ background:G, color:'#000', border:'none', padding:'7px 18px', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13 }}>Get Started</button>
        </div>
      </nav>
      <div style={{ overflow:'hidden', borderBottom:'1px solid #1a1a28' }}>
        <div style={{ display:'flex', gap:48, padding:'10px 0', width:'max-content', animation:'ticker 30s linear infinite' }}>
          {[...LANDING_TRADES,...LANDING_TRADES].map((t,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, whiteSpace:'nowrap' }}>
              <span style={{ background:SPORT_CLR[t.sport]||'#3b82f6', color:'#000', fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:4 }}>{t.sport}</span>
              <span style={{ color:M, fontSize:13 }}>{t.team}</span>
              <span style={{ color:G, fontSize:13, fontWeight:700 }}>{t.change}</span>
            </div>
          ))}
        </div>
      </div>
      <section style={{ padding:'96px 48px 80px', maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:64, alignItems:'center' }}>
        <div>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'#00e87a22', border:'1px solid #00e87a44', borderRadius:20, padding:'5px 14px', fontSize:12, color:G, fontWeight:600, marginBottom:28 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:G, display:'inline-block', animation:'glow 2s infinite' }} /> BOT ACTIVE · LIVE TRADING
          </div>
          <h1 style={{ fontSize:52, fontWeight:800, lineHeight:1.05, letterSpacing:-2, margin:'0 0 24px', color:LP.white }}>Automated sports trading on <span style={{color:G}}>Kalshi</span></h1>
          <p style={{ fontSize:17, color:M, lineHeight:1.7, margin:'0 0 40px', maxWidth:480 }}>Our bot scans 500+ Kalshi prediction markets every 15 seconds, finding edges and placing trades automatically. You keep 100% of the profits.</p>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <button onClick={onSignIn} style={{ background:G, color:'#000', border:'none', padding:'14px 28px', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:16 }}>Get Started</button>
            <span style={{ color:M, fontSize:13 }}>No credit card required</span>
          </div>
          <div style={{ display:'flex', gap:24, marginTop:32 }}>
            {['CFTC Regulated','Cancel Anytime','Cancel anytime'].map(l => (
              <div key={l} style={{ display:'flex', alignItems:'center', gap:6, color:M, fontSize:13 }}><span style={{color:G}}>✓</span>{l}</div>
            ))}
          </div>
        </div>
        <div style={{ background:CA, border:'1px solid #1a1a28', borderRadius:16, overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #1a1a28', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:G, display:'inline-block', boxShadow:'0 0 8px #00e87a' }} />
              <span style={{ fontSize:13, fontWeight:600 }}>Live Trade Feed</span>
            </div>
            <span style={{ fontSize:11, color:M }}>SCANNING 500+ MARKETS</span>
          </div>
          <div>
            {LANDING_TRADES.slice(0,5).map((t,i) => (
              <div key={i} style={{ padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', background:i===tradeIdx%5?'#00e87a08':'transparent', borderLeft:i===tradeIdx%5?'2px solid #00e87a':'2px solid transparent', transition:'all 0.3s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ background:SPORT_CLR[t.sport]||'#3b82f6', color:'#000', fontSize:10, fontWeight:800, padding:'2px 6px', borderRadius:4 }}>{t.sport}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{t.team}</div>
                    <div style={{ fontSize:11, color:M }}>{t.from} to {t.to}</div>
                  </div>
                </div>
                <span style={{ color:G, fontWeight:700, fontSize:14 }}>{t.change}</span>
              </div>
            ))}
          </div>
          <div style={{ padding:'12px 20px', borderTop:'1px solid #1a1a28', display:'flex', gap:6, alignItems:'center' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:G, display:'inline-block' }} />
            <span style={{ fontSize:11, color:M }}>Scanning live markets every 15 seconds...</span>
          </div>
        </div>
      </section>
      <div style={{ borderTop:'1px solid #1a1a28', borderBottom:'1px solid #1a1a28', padding:'40px 48px', background:CA }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:40 }}>
          {[['500+','Markets Scanned Daily'],['15s','Scan Interval'],['3','Sports Covered'],['CFTC','Regulated']].map(([v,l]) => (
            <div key={l} style={{textAlign:'center'}}>
              <div style={{ fontSize:40, fontWeight:800, color:G, letterSpacing:-2 }}>{v}</div>
              <div style={{ fontSize:12, color:M, marginTop:4, textTransform:'uppercase', letterSpacing:0.5 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      <section style={{ padding:'96px 48px', maxWidth:1100, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:64 }}>
          <div style={{ fontSize:12, fontWeight:700, color:G, letterSpacing:2, textTransform:'uppercase', marginBottom:16 }}>How It Works</div>
          <h2 style={{ fontSize:40, fontWeight:800, letterSpacing:-1.5, margin:0 }}>Three steps to automated trading</h2>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:32 }}>
          {[{s:'01',t:'Connect Your Account',d:'Sign up, choose your plan, and connect your Kalshi API key. Takes 5 minutes. No technical knowledge needed.'},{s:'02',t:'Bot Starts Trading',d:'Your bot immediately begins scanning live markets across NBA, MLB, and NHL, finding edges and placing trades automatically.'},{s:'03',t:'Watch It Run',d:'Monitor your dashboard in real time. See every trade, your win rate, and P&L. The bot runs 24/7.'}].map(({s,t,d}) => (
            <div key={s} style={{ background:CA, border:'1px solid #1a1a28', borderRadius:16, padding:32 }}>
              <div style={{ fontSize:48, fontWeight:800, color:G, opacity:0.2, marginBottom:16 }}>{s}</div>
              <h3 style={{ fontSize:20, fontWeight:700, margin:'0 0 12px', color:LP.white }}>{t}</h3>
              <p style={{ fontSize:15, color:M, lineHeight:1.7, margin:0 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>
      <div style={{ background:CA, borderTop:'1px solid #1a1a28', borderBottom:'1px solid #1a1a28', padding:'48px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ fontSize:12, fontWeight:700, color:M, letterSpacing:2, textTransform:'uppercase', marginBottom:24 }}>Sports Coverage</div>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            {[{sp:'NBA',mk:'Game winners · Player props · Spreads · Totals · Overtime',cl:'#F97316'},{sp:'MLB',mk:'Game winners · Run line · Totals · YRFI · First 5 innings · Player props',cl:'#3B82F6'},{sp:'NHL',mk:'Game winners · Puck line · Total goals · Overtime · Player props',cl:'#06B6D4'}].map(({sp,mk,cl}) => (
              <div key={sp} style={{ flex:1, minWidth:280, background:LP.bg, border:'1px solid #1a1a28', borderRadius:12, padding:'20px 24px' }}>
                <span style={{ background:cl, color:'#000', fontSize:11, fontWeight:800, padding:'3px 8px', borderRadius:5, display:'inline-block', marginBottom:10 }}>{sp}</span>
                <div style={{ fontSize:13, color:M, lineHeight:1.8 }}>{mk}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <section style={{ padding:'96px 48px', maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'center' }}>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:G, letterSpacing:2, textTransform:'uppercase', marginBottom:16 }}>Why Kalshi</div>
          <h2 style={{ fontSize:40, fontWeight:800, letterSpacing:-1.5, margin:'0 0 24px' }}>The edge is in the market structure</h2>
          <p style={{ fontSize:16, color:M, lineHeight:1.8, margin:'0 0 20px' }}>Kalshi is a CFTC-designated contract market, same oversight as the NYSE. Unlike sportsbooks, Kalshi cannot ban winning traders or limit accounts.</p>
          <p style={{ fontSize:16, color:M, lineHeight:1.8, margin:0 }}>Odds are set by retail traders, not sharp bookmakers. That creates consistent mispricings, especially during live games when the market lags by 15-30 seconds.</p>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {[{ti:'CFTC Regulated',de:'Same oversight as NYSE and CME. Legal in all 50 states.'},{ti:"Can't Ban Winners",de:'No account limits. No shadow bans. Your edge stays yours.'},{ti:'Retail Pricing',de:'Odds set by retail traders, not sharp books. Mispricings happen constantly.'},{ti:'Live Market Lag',de:'Markets take 15-30 seconds to update during live games. That is your window.'}].map(({ti,de}) => (
            <div key={ti} style={{ background:CA, border:'1px solid #1a1a28', borderRadius:12, padding:'16px 20px' }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{ti}</div>
              <div style={{ fontSize:13, color:M, lineHeight:1.6 }}>{de}</div>
            </div>
          ))}
        </div>
      </section>
      <section style={{ padding:'96px 48px', background:CA, borderTop:'1px solid #1a1a28' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:64 }}>
            <div style={{ fontSize:12, fontWeight:700, color:G, letterSpacing:2, textTransform:'uppercase', marginBottom:16 }}>Pricing</div>
            <h2 style={{ fontSize:40, fontWeight:800, letterSpacing:-1.5, margin:'0 0 16px' }}>Simple, transparent pricing</h2>
            <p style={{ color:M, fontSize:16, margin:'0 0 24px' }}>Cancel anytime · No contracts</p>
            <div style={{ display:'inline-flex', alignItems:'center', background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:40, padding:4, gap:4 }}>
              <button onClick={() => setAnnual(false)} style={{ padding:'8px 20px', borderRadius:36, border:'none', background:!annual?G:'transparent', color:!annual?'#000':M, fontWeight:700, fontSize:13, cursor:'pointer' }}>Monthly</button>
              <button onClick={() => setAnnual(true)} style={{ padding:'8px 20px', borderRadius:36, border:'none', background:annual?G:'transparent', color:annual?'#000':M, fontWeight:700, fontSize:13, cursor:'pointer' }}>Annual <span style={{ fontSize:11, background:'rgba(0,232,122,0.2)', color:G, padding:'2px 6px', borderRadius:10, marginLeft:4 }}>Save 25%</span></button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:24 }}>
            {LPLANS.map(plan => { const planUrl = annual && plan.annualUrl ? plan.annualUrl : plan.url; return (
              <div key={plan.name} onMouseEnter={() => setHovered(plan.name)} onMouseLeave={() => setHovered(null)} style={{ background:LP.bg, border:'2px solid '+(hovered===plan.name||plan.popular?plan.color:'#1a1a28'), borderRadius:20, padding:32, position:'relative', transform:plan.popular?'scale(1.03)':'none', transition:'border-color 0.2s' }}>
                {plan.popular && <div style={{ position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:plan.color, color:'#000', padding:'4px 16px', borderRadius:20, fontSize:11, fontWeight:800, whiteSpace:'nowrap' }}>MOST POPULAR</div>}
                <div style={{ fontSize:12, fontWeight:700, color:plan.color, marginBottom:8, textTransform:'uppercase' }}>{plan.name}</div>
                <div style={{marginBottom:4}}>
                  <span style={{ fontSize:48, fontWeight:800, color:LP.white, letterSpacing:-2 }}>${annual ? Math.round(plan.price * 12 * 0.75 / 12) : plan.price}</span>
                  <span style={{ color:M, fontSize:15 }}>/mo</span>
                </div>
                {annual && <div style={{ fontSize:12, color:G, marginBottom:4 }}>billed ${Math.round(plan.price * 12 * 0.75)}/year</div>}
                <p style={{ fontSize:13, color:M, margin:'0 0 24px' }}>{plan.desc}</p>
                <ul style={{ listStyle:'none', padding:0, margin:'0 0 32px' }}>
                  {plan.features.map(f => (<li key={f} style={{ color:LP.text, marginBottom:10, display:'flex', gap:10, fontSize:14 }}><span style={{color:plan.color}}>✓</span>{f}</li>))}
                </ul>
                <button onClick={() => window.open(planUrl,'_blank')} style={{ width:'100%', padding:'13px 0', borderRadius:10, border:'2px solid '+plan.color, background:plan.popular?plan.color:'transparent', color:plan.popular?'#000':plan.color, fontSize:15, fontWeight:700, cursor:'pointer' }}>Get Started</button>
              </div>
            ); })}
          </div>
        </div>
      </section>
      <section style={{ padding:'64px 48px', background:'#060608', borderTop:'1px solid #1a1a28' }}>
        <div style={{ maxWidth:900, margin:'0 auto', textAlign:'center' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#8b5cf6', letterSpacing:2, textTransform:'uppercase', marginBottom:16 }}>For Developers</div>
          <h2 style={{ fontSize:36, fontWeight:800, letterSpacing:-1, margin:'0 0 16px' }}>API Access</h2>
          <p style={{ color:'#6b7280', fontSize:16, margin:'0 0 40px', maxWidth:600, marginLeft:'auto', marginRight:'auto' }}>Direct access to our signal engine. Bring your own execution layer. Perfect for funds, prop shops, and developers who want raw edges.</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:24, marginBottom:40 }}>
            {[['500+','Markets scanned every 15s'],['10+','Active edge engines'],['8%+','Minimum edge threshold']].map(([val,label]) => (
              <div key={label} style={{ background:'#0d0d12', border:'1px solid #1a1a28', borderRadius:16, padding:'28px 20px' }}>
                <div style={{ fontSize:36, fontWeight:800, color:'#8b5cf6', marginBottom:8 }}>{val}</div>
                <div style={{ color:'#6b7280', fontSize:13 }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ background:'#0d0d12', border:'1px solid #2d2040', borderRadius:20, padding:40, marginBottom:32, textAlign:'left' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
              {[['Real-time signal feed','Pre-game + live edges across NBA/MLB/NHL'],['Vegas line comparison','We diff Kalshi vs DraftKings/FanDuel instantly'],['Injury alerts','Star player status changes before markets price in'],['Edge confidence scores','Kelly-sized recommendations per signal'],['WebSocket stream','Sub-second delivery of new signals'],['Custom thresholds','Set your own edge floor per sport']].map(([title,desc]) => (
                <div key={title} style={{ display:'flex', gap:12 }}>
                  <span style={{ color:'#8b5cf6', fontSize:18, marginTop:2 }}>✓</span>
                  <div><div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>{title}</div><div style={{ color:'#6b7280', fontSize:13 }}>{desc}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:24 }}>
            <div style={{ fontSize:48, fontWeight:800 }}>$499<span style={{ fontSize:16, color:'#6b7280', fontWeight:400 }}>/mo</span></div>
            <button onClick={() => window.open('https://discord.gg/kmMy89fr','_blank')} style={{ padding:'14px 32px', borderRadius:12, border:'none', background:'#8b5cf6', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer' }}>Join Discord for Access</button>
          </div>
        </div>
      </section>

      <section style={{ padding:'96px 48px', maxWidth:800, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:64 }}>
          <div style={{ fontSize:12, fontWeight:700, color:G, letterSpacing:2, textTransform:'uppercase', marginBottom:16 }}>FAQ</div>
          <h2 style={{ fontSize:40, fontWeight:800, letterSpacing:-1.5, margin:0 }}>Frequently asked questions</h2>
        </div>
        {LFAQS.map(({q,a},i) => (
          <div key={i} onClick={() => setOpenFaq(openFaq===i?null:i)} style={{ borderBottom:'1px solid #1a1a28', cursor:'pointer' }}>
            <div style={{ padding:'24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:600, fontSize:16 }}>{q}</span>
              <span style={{ color:M, fontSize:20, transform:openFaq===i?'rotate(45deg)':'none', transition:'transform 0.2s', display:'inline-block' }}>+</span>
            </div>
            {openFaq===i && <div style={{ padding:'0 0 24px', color:M, fontSize:15, lineHeight:1.8 }}>{a}</div>}
          </div>
        ))}
      </section>
      <div style={{ background:'#5865F222', border:'1px solid #5865F244', margin:'0 48px 48px', borderRadius:20, padding:48, textAlign:'center' }}>
        <h3 style={{ fontSize:28, fontWeight:800, margin:'0 0 12px' }}>Join the community</h3>
        <p style={{ color:M, margin:'0 0 28px', fontSize:15 }}>Real-time trade alerts, win screenshots, and support from other KalshiBot Pro users.</p>
        <a href="https://discord.gg/H6KyBgP338" target="_blank" rel="noopener noreferrer" style={{ display:'inline-block', background:'#5865F2', color:'#fff', padding:'12px 32px', borderRadius:10, fontWeight:700, fontSize:15, textDecoration:'none' }}>Join Discord</a>
      </div>
      <footer style={{ padding:'32px 48px', borderTop:'1px solid #1a1a28', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:16 }}>
        <span style={{ fontWeight:700, fontSize:15, color:LP.white }}>⚡ KalshiBot Pro</span>
        <div style={{ display:'flex', gap:24 }}>
          {[['Twitter','https://x.com/KalshiiBot'],['TikTok','https://tiktok.com/@kalshibot'],['Discord','https://discord.gg/H6KyBgP338']].map(([n,u]) => (
            <a key={n} href={u} target="_blank" rel="noopener noreferrer" style={{ color:M, fontSize:13, textDecoration:'none' }}>{n}</a>
          ))}
        </div>
        <div style={{ fontSize:12, color:M }}>2026 KalshiBot Pro · Not financial advice · Trading involves risk</div>
      </footer>
    </div>
  );
}


export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('landing');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user || null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      if (session?.user) fetchProfile(session.user.id);
      else { setProfile(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    const { data } = await supabase.from('profiles').select('*').eq('id', userId).single();
    setProfile(data); setLoading(false);
  }

  if (loading) return <div style={{ minHeight:'100vh', background:'#080808', display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ color:'#00ff88', fontSize:14, fontFamily:'monospace' }}>Loading...</div></div>;

  if (!user) {
    if (page === 'auth') return <AuthPage supabase={supabase} onBack={() => setPage('landing')} />;
    return <LandingPage onSignIn={() => setPage('auth')} />;
  }

  if (!profile?.kalshi_api_key_id) return <OnboardingPage supabase={supabase} user={user} profile={profile} onComplete={() => fetchProfile(user.id)} />;

  return <Dashboard supabase={supabase} user={user} profile={profile} onSignOut={async () => { await supabase.auth.signOut(); }} />;
}
